/**
 * @fileoverview AttachmentList component for displaying file attachments.
 * Provides simple and detailed views for file downloads.
 */

'use client';

import { Download, Eye, FileText, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { FilePreviewDialog } from './file-preview-dialog';
import { AttachmentPreviewDialog } from '@/components/chat/file/attachment-preview-dialog';
import { getBadgeColor } from '../../../tool-views/util';
import { FilePreviewDialog as UnifiedFilePreviewDialog, type FileSource } from '@/components/file-preview';
import { coworkFileSource } from '@/components/cowork/cowork-file-source';
import { useCoworkStore } from '@/store/cowork-store';
import { isDesktopApp } from '@/lib/desktop';
import { getChatIdFromPathname } from '@/lib/chat-icon';
import { getAuthHeader } from '@/utils';
import { Attachment } from '@/types/chat-message';

/**
 * Coerce a single attachment entry to a string path/URL. Some tool payloads
 * (complete / render_ui / artifact) hand us attachment objects
 * (`{ url }` | `{ filename }` | `{ name }` | `{ path }`) instead of bare
 * strings. The list components index into the value as a string
 * (`urlOrFilename.includes('/')`), so a non-string entry throws. Normalizing
 * here keeps every caller safe.
 */
export function toAttachmentString(item: unknown): string {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const o = item as Record<string, unknown>;
    const v = o.url ?? o.filename ?? o.name ?? o.path;
    if (typeof v === 'string') return v;
  }
  return '';
}

/**
 * Parse attachments input into a consistent array format.
 */
export function parseAttachments(attachments: string | string[] | undefined): string[] {
  if (!attachments) return [];
  if (Array.isArray(attachments)) return attachments.map(toAttachmentString).filter(Boolean);
  if (typeof attachments === 'string') {
    return attachments.split(',').map((a) => a.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Handle file download from a chat workspace.
 *
 * Prefers the chat-scoped URL `/workspace-files/chat/{chatId}/...` when
 * a chatId is available — that route resolves files via the canonical
 * `(workspaceId, chatId)` lookup and falls back to the S3 sync manifest
 * when the local sandbox is gone. The legacy sandboxId-keyed URL is kept
 * as a fallback for surfaces (bubble/preview) where chatId isn't in the
 * URL.
 */
async function handleFileDownload(
  filename: string,
  chatId: string | undefined,
  sandboxId: string | undefined,
  setLoadingStates: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void
) {
  if (!chatId && !sandboxId) {
    toast.error('Cannot resolve workspace for this file');
    return;
  }

  const loadingKey = `${filename}-download`;
  setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));

  try {
    const baseUrl = chatId
      ? `/api/workspace-files/chat/${chatId}/files/content`
      : `/api/workspace-files/${sandboxId}/files/content`;
    // This is a programmatic fetch, so it can carry the Bearer header directly
    // (the /workspace-files routes now require auth — see #1125/#1126).
    const authHeader = getAuthHeader();
    const response = await fetch(
      `${baseUrl}?path=${encodeURIComponent(filename)}&download=true`,
      { headers: authHeader ? { Authorization: authHeader } : {} }
    );

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.split('/').pop() || filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename.split('/').pop()}`);
  } catch (error) {
    console.error('Failed to download file:', error);
    toast.error(`Failed to download ${filename.split('/').pop()}`);
  } finally {
    setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
  }
}

interface SimpleAttachmentListProps {
  /** Array of filenames */
  attachments: string[];
  /** Sandbox ID for file operations */
  sandboxId?: string;
}

/**
 * Simple attachment tiles for the ask tool.
 */
export function SimpleAttachmentList({ attachments, sandboxId }: SimpleAttachmentListProps) {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  // See file-preview-dialog.tsx for why pathname is the source of truth.
  const chatIdFromUrl = getChatIdFromPathname();

  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {attachments.map((filename, index) => {
        const isDownloadLoading = loadingStates[`${filename}-download`] || false;

        return (
          <div
            key={index}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card p-2 pr-1 transition-colors hover:bg-accent"
          >
            <FileText className="size-4 text-primary shrink-0" />
            <div className="max-w-[200px]">
              <p className="text-sm font-medium text-card-foreground truncate">
                {filename}
              </p>
            </div>
            <button
              onClick={() => handleFileDownload(filename, chatIdFromUrl, sandboxId, setLoadingStates)}
              disabled={isDownloadLoading}
              className="ml-1 p-1 rounded text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:text-green-400 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download file"
            >
              {isDownloadLoading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}

interface DetailedAttachmentListProps {
  /** Array of URLs or filenames */
  attachments: string[];
  /** Sandbox ID for file operations */
  sandboxId?: string;
}

/**
 * Detailed file cards for the complete tool.
 */
export function DetailedAttachmentList({ attachments, sandboxId }: DetailedAttachmentListProps) {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [selectedUrlAttachment, setSelectedUrlAttachment] = useState<Attachment | null>(null);
  // CoWork: local files live on the device, not a cloud sandbox — preview them
  // through the device bridge + the unified dialog instead of the sandbox path.
  const coworkDeviceId = useCoworkStore((s) => (s.mode === 'cowork' ? s.deviceId : null));
  const coworkActive = isDesktopApp() && !!coworkDeviceId;
  const [coworkSource, setCoworkSource] = useState<FileSource | null>(null);
  // See file-preview-dialog.tsx for why pathname is the source of truth.
  const chatIdFromUrl = getChatIdFromPathname();

  if (attachments.length === 0) return null;

  return (
    <>
      <div className="flex flex-wrap gap-3 mt-2">
        {attachments.map((rawAttachment, index) => {
          // Guard the direct-call path (message.tsx) which bypasses
          // parseAttachments — a non-string entry would throw on `.includes`.
          const urlOrFilename = toAttachmentString(rawAttachment);
          // Extract filename from URL or use as-is
          const rawFilename = urlOrFilename.includes('/')
            ? urlOrFilename.split('/').pop() || 'file'
            : urlOrFilename;

          // Clean up filename - remove UUID prefix if present
          const cleanFilename = rawFilename.replace(
            /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i,
            ''
          );

          // Get file extension for badge
          const extension = cleanFilename.split('.').pop()?.toUpperCase() || 'FILE';
          const isUrl = urlOrFilename.startsWith('http');
          const isDownloadLoading = loadingStates[`${rawFilename}-download`] || false;

          const handleCardClick = () => {
            if (isUrl) {
              // Create attachment object for URL-based preview
              setSelectedUrlAttachment({
                name: cleanFilename,
                url: urlOrFilename,
                contentType: '',
                size: 0,
                type: 'file',
              });
            } else if (coworkActive) {
              // Read the file from the connected device folder.
              setCoworkSource(coworkFileSource(urlOrFilename));
            } else {
              setSelectedFile(urlOrFilename);
            }
          };

          return (
            <div
              key={index}
              onClick={handleCardClick}
              className="group relative flex items-center gap-3 rounded-xl border border-border bg-gradient-to-br from-card to-muted/30 p-3 pr-4 min-w-[200px] max-w-[320px] transition-all duration-200 cursor-pointer hover:border-primary/50 hover:shadow-md"
            >
            {/* File icon with background */}
            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 dark:bg-primary/20">
              <FileText className="size-5 text-primary" />
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-card-foreground truncate leading-tight">
                {cleanFilename}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${getBadgeColor(extension)}`}
                >
                  {extension}
                </span>
                <span className="text-xs text-muted-foreground">Click to preview</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1">
              {/* Preview button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick();
                }}
                className="flex-shrink-0 p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                title="Preview file"
              >
                <Eye className="size-4" />
              </button>

              {/* Download button */}
              {isUrl ? (
                <a
                  href={urlOrFilename}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-shrink-0 p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                  title="Download file"
                >
                  <Download className="size-4" />
                </a>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileDownload(rawFilename, chatIdFromUrl, sandboxId, setLoadingStates);
                  }}
                  disabled={isDownloadLoading}
                  className="flex-shrink-0 p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download file"
                >
                  {isDownloadLoading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Download className="size-4" />
                  )}
                </button>
              )}
            </div>
          </div>
        );
      })}
      </div>

      {/* File Preview Dialog for sandbox files */}
      {selectedFile && (
        <FilePreviewDialog
          filename={selectedFile}
          sandboxId={sandboxId}
          open={selectedFile !== null}
          onOpenChange={(open) => !open && setSelectedFile(null)}
        />
      )}

      {/* CoWork: preview device files via the unified dialog (all types + fullscreen) */}
      {coworkSource && (
        <UnifiedFilePreviewDialog
          source={coworkSource}
          open={!!coworkSource}
          onOpenChange={(open) => !open && setCoworkSource(null)}
        />
      )}

      {/* Attachment Preview Dialog for URL-based files */}
      {selectedUrlAttachment && (
        <AttachmentPreviewDialog
          attachment={selectedUrlAttachment}
          open={selectedUrlAttachment !== null}
          onOpenChange={(open) => !open && setSelectedUrlAttachment(null)}
        />
      )}
    </>
  );
}
